import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  alpha,
  Button,
  useTheme,
} from '@mui/material';
import { 
  Refresh, 
  Warning,
  CheckCircle,
  Shield,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { riskAPI } from '../services/api';

// Import new components
import RiskGauge from '../components/risk-management/RiskGauge';
import ExposureTimeline from '../components/risk-management/ExposureTimeline';
import DrawdownMonitor from '../components/risk-management/DrawdownMonitor';
import RiskMatrix from '../components/risk-management/RiskMatrix';
import TradingGates from '../components/risk-management/TradingGates';
import PortfolioHealth from '../components/risk-management/PortfolioHealth';
import RiskMetricsGrid from '../components/risk-management/RiskMetricsGrid';
import RiskManagementSkeleton from '../components/risk-management/RiskManagementSkeleton';

function RiskManagement() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [signals, setSignals] = useState(null);
  const [equityHistory, setEquityHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchData();
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [signalsRes, historyRes] = await Promise.allSettled([
        riskAPI.getDailySignals(),
        riskAPI.getEquityHistory(30).catch(() => ({ data: { history: [] } })),
      ]);

      if (signalsRes.status === 'fulfilled') {
        setSignals(signalsRes.value.data);
        setLastUpdate(new Date());
      } else {
        setError(signalsRes.reason?.response?.data?.detail || 'Failed to load risk signals');
      }

      if (historyRes.status === 'fulfilled') {
        setEquityHistory(historyRes.value.data?.history || []);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching risk data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading && !signals) {
    return <RiskManagementSkeleton />;
  }


  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
    }}>
      {/* Header Section - Like Downtrend */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                width: 64,
                height: 64,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                boxShadow: `0 8px 24px ${alpha('#6366f1', 0.3)}`,
                flexShrink: 0,
                }}
              >
              <Shield sx={{ color: 'white', fontSize: 36 }} />
              </Box>
              <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: -0.5 }}>
                {t('risk.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {signals?.can_enter ? 
                    <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} /> : 
                    <Warning sx={{ fontSize: 16, color: '#f59e0b' }} />
                  }
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: signals?.can_enter ? '#10b981' : '#f59e0b',
                      }}
                    >
                      {signals?.can_enter ? t('risk.entryAllowed') : t('risk.entryBlocked')}
                    </Typography>
                </Box>
                {lastUpdate && (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      •
                </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {lastUpdate.toLocaleTimeString()}
                </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right: Actions */}
            <Button
              variant="outlined"
            startIcon={<Refresh className={refreshing ? 'spinning' : ''} sx={{ fontSize: 16 }} />}
              onClick={handleRefresh}
              disabled={refreshing}
              size="small"
              sx={{
                minWidth: 110,
                height: 32,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                bgcolor: 'background.paper',
                color: theme.palette.primary.main,
                borderWidth: 1,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: 0.75,
                px: 1.5,
                transition: 'all 0.2s ease',
                '& .MuiSvgIcon-root': {
                  color: theme.palette.primary.main,
                },
                '&:hover': {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  color: theme.palette.primary.main,
                },
                '&:active': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
                '&:disabled': {
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  color: theme.palette.text.disabled,
                  opacity: 0.6,
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.text.disabled,
                  },
                },
              }}
            >
            {refreshing ? t('common.refreshing') : t('common.refresh')}
            </Button>
          </Box>
        </Box>

      {/* Main Content - CSS Grid for full width */}
      <Box sx={{ 
        flex: 1,
        width: '100%',
      }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2.5,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {/* Market Info Banner - Compact */}
        {signals && (
          <Box sx={{ 
            mb: 2.5,
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            bgcolor: signals?.regime === 'RISK_ON' 
              ? alpha('#10b981', 0.06)
              : alpha('#ef4444', 0.06),
            border: '1px solid',
            borderColor: signals?.regime === 'RISK_ON'
              ? alpha('#10b981', 0.15)
              : alpha('#ef4444', 0.15),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: signals?.regime === 'RISK_ON'
                  ? alpha('#10b981', 0.12)
                  : alpha('#ef4444', 0.12),
                color: signals?.regime === 'RISK_ON' ? '#10b981' : '#ef4444',
                flexShrink: 0,
              }}
            >
              {signals?.regime === 'RISK_ON' ? 
                <CheckCircle sx={{ fontSize: 18 }} /> : 
                <Warning sx={{ fontSize: 18 }} />
              }
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  color: 'text.primary',
                }}
              >
                {signals?.regime === 'RISK_ON' 
                  ? t('risk.marketPositive')
                  : t('risk.marketHighRisk')}
                <Typography 
                  component="span" 
                  sx={{ 
                    ml: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  {signals?.can_enter 
                    ? t('risk.canEnterNew')
                    : t('risk.shouldNotEnter')}
                  {' • '}
                  {t('risk.avgDownRisk')}: {(((signals?.down_risk_10 + signals?.down_risk_20 + signals?.down_risk_60) / 3) * 100).toFixed(1)}%
                </Typography>
              </Typography>
            </Box>
          </Box>
        )}

        {/* CSS Grid Layout - Optimized spacing */}
        <Box sx={{
          display: 'grid',
          gap: { xs: 2, sm: 2.5 },
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gridAutoRows: 'auto',
        }}>
          {/* Row 1: Critical Metrics - 3 equal columns */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1', lg: '1' } }}>
            <RiskGauge 
              title={t('risk.downRiskScore')}
              value={(signals?.down_risk_20 || 0) * 100}
              max={100}
              thresholds={[30, 50, 70]}
              colors={['#10b981', '#f59e0b', '#ef4444', '#991b1b']}
              subtitle={`10D: ${((signals?.down_risk_10 || 0) * 100).toFixed(1)}% | 20D: ${((signals?.down_risk_20 || 0) * 100).toFixed(1)}% | 60D: ${((signals?.down_risk_60 || 0) * 100).toFixed(1)}%`}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', sm: '2', lg: '2' } }}>
            <RiskGauge 
              title={t('risk.portfolioExposure')}
              value={(signals?.exposure || 0) * 100}
              max={100}
              thresholds={[40, 70, 100]}
              colors={['#ef4444', '#f59e0b', '#10b981']}
              subtitle={`Base: ${((signals?.base_exposure || 0) * 100).toFixed(1)}% | Cap: ${((signals?.cap || 0) * 100).toFixed(1)}%`}
              inverted={true}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '3' } }}>
            <DrawdownMonitor 
              currentDD={(signals?.dd || 0) * 100}
              maxDD={signals?.circuit_breaker?.[3]?.threshold || -10}
              equity={signals?.equity}
              peakEquity={signals?.peak_equity}
              marketDD={signals?.market_dd}
              marketPeak={signals?.market_dd_peak}
              marketCurrent={signals?.market_dd_current}
              circuitBreaker={signals?.circuit_breaker}
              cbLevel={signals?.cb_level}
            />
          </Box>

          {/* Row 2: Portfolio Health & Trading Gates */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 3' } }}>
            <PortfolioHealth 
              equity={signals?.equity}
              peakEquity={signals?.peak_equity}
              drawdown={signals?.dd}
              exposure={signals?.exposure}
              history={equityHistory}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '3' } }}>
            <TradingGates 
              canEnter={signals?.can_enter}
              marketGate={signals?.market_gate}
              noTrade={signals?.no_trade}
              regime={signals?.regime}
              downRisk20={signals?.down_risk_20}
              downRisk60={signals?.down_risk_60}
            />
          </Box>

          {/* Row 3: Risk Metrics Grid - Full Width */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 4' } }}>
            <RiskMetricsGrid signals={signals} />
          </Box>

          {/* Row 4: Risk Matrix & Exposure Timeline */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '1 / 2' } }}>
            <RiskMatrix 
              downRisk10={signals?.down_risk_10}
              downRisk20={signals?.down_risk_20}
              downRisk60={signals?.down_risk_60}
              exposure={signals?.exposure}
              drawdown={signals?.dd}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3', lg: '2 / 4' } }}>
            <ExposureTimeline history={equityHistory} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default RiskManagement;
